import React, { useCallback, useContext, useEffect } from "react";
import { Menu, Tooltip } from "antd";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setActiveMenuItem } from "../../../redux/modules/app/actions";
import { activeMenuItemSelector } from "../../../redux/modules/app/selectors";

import "./styles.css";
import { AppContext } from "../../../context/AppContext";
import { ROUTES, regionOverviewPath, procuringEntityPackagesPath, procuringEntityReportsPath, projectOverviewPath } from "../../../Util/routes";

const SideMenu = (props) => {
  const { baseUrl, isMobile, onMenuItemClick } = props;
  const activeMenuItem = useSelector(activeMenuItemSelector);
  const {
    sideMenuKeys,
    menuItems: items,
  selectedProcuringEntity,
  selectedProject,
    selectedRegionId,
  // Report-centric navigation: require a selected report to enable core sections
  selectedReportID,
  } = useContext(AppContext);
  const dispatch = useDispatch();

  const history = useHistory();
  const location = useLocation(); // Get the current location

  const computeBaseUrl = useCallback(() => {
    if (baseUrl === "/") {
  // Base project route must use the project id
  return `/project/${selectedProject ?? ''}`;
    } else return baseUrl;
  }, [baseUrl, selectedProject]);

  const handleOnMenuItemClick = (e) => {
    dispatch(setActiveMenuItem(e.key));
  // Guard: core sections require a selected report AND a derived project id
  const keysRequiringReport = [sideMenuKeys.project, sideMenuKeys.packages, sideMenuKeys.reports];
  const missingReportOrProject = !selectedReportID || !selectedProject;
    if (missingReportOrProject && keysRequiringReport.includes(e.key)) {
      return;
    }
    // Explicitly route Project Overview to absolute project path to avoid relative routing under /settings
    if (e.key === sideMenuKeys.project) {
      history.push(projectOverviewPath(selectedProject));
      return;
    }
    if (e.key === sideMenuKeys.regionOverview && selectedRegionId) {
      history.push(regionOverviewPath(selectedProject, selectedRegionId));
    } else if (e.key === sideMenuKeys.packages) {
      // Fallback: if procuring entity is not set, do nothing and rely on filter modal enforcement
      if (!selectedProcuringEntity) {
        return;
      }
      history.push(procuringEntityPackagesPath(selectedProject, selectedProcuringEntity));
    } else if (e.key === sideMenuKeys.reports) {
      // Progress Reports are nested under procuring_entity
      if (!selectedProcuringEntity) {
        return;
      }
      history.push(procuringEntityReportsPath(selectedProject, selectedProcuringEntity));
    } else if (e.key === sideMenuKeys.users) {
      history.push(ROUTES.USERS);
    } else if (e.key === sideMenuKeys.roles) {
      history.push(ROUTES.ROLES);
    } else if (e.key === sideMenuKeys.documenents) {
      history.push(ROUTES.DOCUMENTS);
    } else if (e.key === sideMenuKeys.exceptions) {
      history.push(ROUTES.EXCEPTIONS);
    } else if (e.key === sideMenuKeys.settings) {
      history.push(ROUTES.SETTINGS);
    } else if (e.key === sideMenuKeys.projectManagement) {
      history.push(ROUTES.PROJECT_MANAGEMENT);
    } else {
      history.push(`${computeBaseUrl()}/${e.key}`);
    }
    console.log(
      "computeBaseUrl##: ",
      computeBaseUrl(),
      "section##: ",
      e.key,
      "path##: ",
      `${computeBaseUrl()}/${e.key}`
    );

    // Close mobile drawer when menu item is clicked
    if (isMobile && onMenuItemClick) {
      onMenuItemClick();
    }
  };

  // Initial active menu item is derived from current URL via the effect below.

  useEffect(() => {
    // Sync the active menu item with the current URL path (project-id agnostic)
    const pathname = location.pathname || '';
    // Normalize base by removing '/project/:id/' prefix if present
    const projectPrefixMatch = pathname.match(/^\/project\/([^/]+)\//);
    const path = projectPrefixMatch ? pathname.slice(projectPrefixMatch[0].length) : pathname.slice(1);
    // Split the path by '/' to handle sub-routes (extracts 'packages', 'overview', etc.)
    const firstPathSegment = path.split("/")[0];

    // If deep-linked to region overview, mark the special menu key
    if (/^regions\//.test(path)) {
      dispatch(setActiveMenuItem(sideMenuKeys.regionOverview));
  } else if (location.pathname.startsWith('/users')) {
    // Highlight Settings when using legacy /users path
    dispatch(setActiveMenuItem(sideMenuKeys.settings));
  } else if (location.pathname.startsWith('/roles')) {
    // Highlight Settings when using legacy /roles path
    dispatch(setActiveMenuItem(sideMenuKeys.settings));
  } else if (location.pathname.startsWith('/documents')) {
      dispatch(setActiveMenuItem(sideMenuKeys.documenents));
  } else if (location.pathname.startsWith('/exceptions')) {
      dispatch(setActiveMenuItem(sideMenuKeys.exceptions));
  } else if (location.pathname.startsWith('/settings')) {
      dispatch(setActiveMenuItem(sideMenuKeys.settings));
  } else if (location.pathname.startsWith('/project-management')) {
      dispatch(setActiveMenuItem(sideMenuKeys.projectManagement));
    } else {
      // Normalize packages deep path with procuring_entity segment
      if (/procuring_entity\/[^/]+\/packages/.test(location.pathname)) {
        dispatch(setActiveMenuItem(sideMenuKeys.packages));
      } else if (/procuring_entity\/[^/]+\/reports/.test(location.pathname)) {
        // Normalize reports deep path (list/detail/create)
        dispatch(setActiveMenuItem(sideMenuKeys.reports));
      } else {
        dispatch(setActiveMenuItem(firstPathSegment || sideMenuKeys.overview));
      }
    }
  }, [
    location.pathname,
    computeBaseUrl,
    sideMenuKeys.overview,
    sideMenuKeys.regionOverview,
    sideMenuKeys.documenents,
    sideMenuKeys.exceptions,
    sideMenuKeys.packages,
    sideMenuKeys.reports,
    sideMenuKeys.settings,
    sideMenuKeys.projectManagement,
    selectedProject,
    dispatch,
  ]);

  // No localStorage persistence for active menu item.

  // Disable core menu items until a report is selected (report-centric UX)
  const hasSelectedReportAndProject = !!selectedReportID && !!selectedProject;

  return (
    <Menu
      mode="inline"
      className={`side-menu ${isMobile ? "mobile-side-menu" : ""}`}
      selectedKeys={[activeMenuItem]}
      onClick={handleOnMenuItemClick}
      items={items.map((item) => {
        // Disable Overview, Packages and Progress Report until a report is selected
        const disableWhenNoReport = !hasSelectedReportAndProject && [
          sideMenuKeys.project,
          sideMenuKeys.packages,
          sideMenuKeys.reports,
        ].includes(item.key);
        const needsReportMsg = "Select a report to continue";
        const label = disableWhenNoReport
          ? (
              <Tooltip title={needsReportMsg} placement="right">
                <span title={needsReportMsg} className="side-menu-label-disabled-hint">{item.label}</span>
              </Tooltip>
            )
          : item.label;
        return {
          ...item,
          label,
          disabled: item.disabled || disableWhenNoReport,
          "data-testid": `${item.key}-menu-item`,
        };
      })}
    />
  );
};

export default SideMenu;
